import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import { useAuth, signedFetch } from "./AuthContext";

const BookingContext = createContext(null);

export function BookingProvider({ children }) {
  const { user, token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [supportQueries, setSupportQueries] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [isLoadingRooms, setIsLoadingRooms] = useState(false);
  const [isLoadingCallbacks, setIsLoadingCallbacks] = useState(false);
  const [isLoadingSupportQueries, setIsLoadingSupportQueries] = useState(false);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);
  const [isLoadingActiveCoupons, setIsLoadingActiveCoupons] = useState(false);

  /**
   * Fetch authenticated user bookings (or master admin bookings list)
   * All requests are HMAC-signed via signedFetch
   */
  const refreshBookings = useCallback(async () => {
    let cloudBookings = [];
    try {
      if (token && user) {
        setIsLoadingBookings(true);
        const isAdm = user.role === "admin";
        const data = await signedFetch({
          action: isAdm ? "adminGetAllBookings" : "getUserBookings",
          token: token,
        });
        if (data && data.success) {
          cloudBookings = data.bookings || [];
        }
      }
    } catch (err) {
      console.warn("Cloud bookings fetch fallback", err);
    } finally {
      setIsLoadingBookings(false);
    }

    const localHk = JSON.parse(localStorage.getItem("hk_bookings") || "[]");
    const localBk = JSON.parse(localStorage.getItem("bookings") || "[]");

    const mergedMap = new Map();
    cloudBookings.forEach((b) => mergedMap.set(b.bookingId || b.id, b));
    localBk.forEach((l) => {
      const key = l.bookingId || l.id;
      if (key) mergedMap.set(key, { ...(mergedMap.get(key) || {}), ...l });
    });
    localHk.forEach((l) => {
      const key = l.bookingId || l.id;
      if (key) mergedMap.set(key, { ...(mergedMap.get(key) || {}), ...l });
    });

    setBookings(Array.from(mergedMap.values()));
  }, [token, user]);

  /**
   * Fetch room availability (admin gets full data, public gets availability)
   */
  const refreshRooms = useCallback(async () => {
    let cloudRooms = [];
    try {
      setIsLoadingRooms(true);
      const isAdm = user && user.role === "admin" && token;

      const payload = isAdm
        ? { action: "adminGetRooms", token: token }
        : { action: "getRoomAvailability" };

      const data = await signedFetch(payload);

      if (data && data.success && Array.isArray(data.rooms)) {
        cloudRooms = data.rooms;
      }
    } catch (err) {
      console.warn("Cloud rooms fetch fallback", err);
    } finally {
      setIsLoadingRooms(false);
    }

    const localRooms = JSON.parse(localStorage.getItem("hk_rooms") || "[]");
    const mergedMap = new Map();

    // Default room templates to guarantee totalInventory is NEVER undefined!
    const defaultTemplates = [
      { roomId: "RM-101", roomType: "Standard Room", pricePerNight: 3999, totalInventory: 10, bookedCount: 0, status: "Available" },
      { roomId: "RM-102", roomType: "Deluxe Room", pricePerNight: 4999, totalInventory: 10, bookedCount: 0, status: "Available" },
      { roomId: "RM-103", roomType: "Super Deluxe", pricePerNight: 7999, totalInventory: 10, bookedCount: 0, status: "Available" },
    ];

    defaultTemplates.forEach((t) => mergedMap.set(t.roomType.toLowerCase().trim(), t));

    cloudRooms.forEach((r) => {
      if (r && r.roomType) {
        const key = r.roomType.toLowerCase().trim();
        const existing = mergedMap.get(key) || {};
        mergedMap.set(key, { ...existing, ...r, totalInventory: r.totalInventory ?? existing.totalInventory ?? 10 });
      }
    });

    localRooms.forEach((l) => {
      if (l && l.roomType) {
        const key = l.roomType.toLowerCase().trim();
        const existing = mergedMap.get(key) || {};
        mergedMap.set(key, { ...existing, ...l, totalInventory: l.totalInventory ?? existing.totalInventory ?? 10 });
      }
    });

    setRooms(Array.from(mergedMap.values()));
  }, [token, user]);

  /**
   * Admin: Fetch master concierge callback inquiries list (Dual Sync)
   */
  const refreshCallbacks = useCallback(async () => {
    let cloudCallbacks = [];
    try {
      if (token && user && user.role === "admin") {
        setIsLoadingCallbacks(true);
        const data = await signedFetch({
          action: "adminGetCallbacks",
          token: token,
        });
        if (data && data.success) {
          cloudCallbacks = data.callbacks || [];
        }
      }
    } catch (err) {
      console.warn("Cloud callbacks fetch fallback", err);
    } finally {
      setIsLoadingCallbacks(false);
    }

    const localCbs = JSON.parse(localStorage.getItem("hk_callbacks") || "[]");
    const mergedMap = new Map();
    cloudCallbacks.forEach((c) => mergedMap.set(c.callbackId || c.id, c));
    localCbs.forEach((l) => {
      const key = l.callbackId || l.id;
      if (key && !mergedMap.has(key)) {
        mergedMap.set(key, l);
      }
    });
    setCallbacks(Array.from(mergedMap.values()));
  }, [token, user]);

  /**
   * Admin: Fetch support queries list (Dual Sync)
   */
  const refreshSupportQueries = useCallback(async () => {
    let cloudQueries = [];
    try {
      if (token && user && user.role === "admin") {
        setIsLoadingSupportQueries(true);
        const data = await signedFetch({
          action: "adminGetSupportQueries",
          token: token,
        });
        if (data && data.success) {
          cloudQueries = data.queries || data.supportQueries || [];
        }
      }
    } catch (err) {
      console.warn("Cloud support queries fetch fallback", err);
    } finally {
      setIsLoadingSupportQueries(false);
    }

    const localSupport = JSON.parse(localStorage.getItem("hk_support_queries") || "[]");
    const localContact = JSON.parse(localStorage.getItem("contact_inquiries") || "[]");
    const mergedMap = new Map();

    cloudQueries.forEach((q) => mergedMap.set(q.queryId || q.id, q));

    [...localSupport, ...localContact].forEach((l) => {
      const key = l.queryId || l.id || l.timestamp;
      if (key && !mergedMap.has(key)) {
        mergedMap.set(key, {
          queryId: key,
          fullName: l.fullName || l.name || "Guest",
          email: l.email || "",
          number: l.number || "",
          message: l.message || "",
          status: l.status || "Pending",
          createdAt: l.createdAt || l.timestamp || new Date().toISOString(),
        });
      }
    });
    setSupportQueries(Array.from(mergedMap.values()));
  }, [token, user]);

  /**
   * Admin: All coupon codes (HMAC-signed)
   */
  const refreshCoupons = useCallback(async () => {
    if (!token || !user || user.role !== "admin") {
      setCoupons([]);
      return;
    }

    try {
      setIsLoadingCoupons(true);
      const data = await signedFetch({
        action: "adminGetCoupons",
        token: token,
      });

      if (data.success) {
        setCoupons(data.coupons || []);
      } else {
        console.warn("Failed to fetch coupons:", data.error);
      }
    } catch (err) {
      console.error("Connection failed while fetching coupons", err);
    } finally {
      setIsLoadingCoupons(false);
    }
  }, [token, user]);

  /**
   * Guest/User: Fetch all active coupon codes (HMAC-signed)
   */
  const refreshActiveCoupons = useCallback(async () => {
    try {
      setIsLoadingActiveCoupons(true);
      const data = await signedFetch({
        action: "getActiveCoupons",
        token: token,
      });

      if (data.success) {
        setActiveCoupons(data.coupons || []);
      } else {
        console.warn("Failed to fetch active coupons:", data.error);
      }
    } catch (err) {
      console.error("Connection failed while fetching active coupons", err);
    } finally {
      setIsLoadingActiveCoupons(false);
    }
  }, [token]);

  // Auto-fetch bookings, rooms, callbacks, support queries, coupons, and active coupons when session starts
  useEffect(() => {
    refreshBookings();
    refreshRooms();
    refreshCallbacks();
    refreshSupportQueries();
    refreshCoupons();
    refreshActiveCoupons();

    const handleStorage = () => {
      refreshBookings();
      refreshRooms();
      refreshCallbacks();
      refreshSupportQueries();
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [refreshBookings, refreshRooms, refreshCallbacks, refreshSupportQueries, refreshCoupons, refreshActiveCoupons]);

  /**
   * Create Booking with High-Speed Optimistic UI Updates (HMAC-signed)
   */
  const createBooking = async (roomType, checkIn, checkOut, adults, children, totalCost) => {
    if (!user || !token) {
      return { success: false, error: "Authentication required to book a room." };
    }

    const tempBookingId = "temp_" + Math.random().toString(36).substring(2, 9);
    const tempBooking = {
      bookingId: tempBookingId,
      roomType,
      checkIn,
      checkOut,
      adults: parseInt(adults),
      children: parseInt(children || 0),
      totalCost: parseFloat(totalCost),
      status: "Syncing...",
      createdAt: new Date().toISOString(),
    };

    setBookings((prev) => [tempBooking, ...prev]);

    try {
      const data = await signedFetch({
        action: "createBooking",
        token: token,
        roomType,
        checkInDate: checkIn,
        checkOutDate: checkOut,
        adults,
        children,
        subtotal: totalCost,
        finalCost: totalCost,
      });

      if (data.success) {
        setBookings((prev) =>
          prev.map((b) =>
            b.bookingId === tempBookingId
              ? { ...b, bookingId: data.bookingId, status: "Pending" }
              : b
          )
        );

        refreshRooms();

        return { success: true, bookingId: data.bookingId };
      } else {
        setBookings((prev) => prev.filter((b) => b.bookingId !== tempBookingId));
        return { success: false, error: data.error || "Booking submission failed." };
      }
    } catch (err) {
      setBookings((prev) => prev.filter((b) => b.bookingId !== tempBookingId));
      return {
        success: false,
        error: "Server connection failed.",
      };
    }
  };

  /**
   * Create callback request (Optimistic UI + Persistent Storage + HMAC-signed)
   */
  const createCallback = async (name, email, number) => {
    const newCb = {
      callbackId: "cb_" + Math.random().toString(36).substring(2, 9),
      fullName: name,
      email: email,
      number: number,
      status: "Pending",
      createdAt: new Date().toISOString(),
    };

    setCallbacks((prev) => [newCb, ...prev]);

    try {
      const existing = JSON.parse(localStorage.getItem("hk_callbacks") || "[]");
      localStorage.setItem("hk_callbacks", JSON.stringify([newCb, ...existing]));
    } catch (e) {
      console.warn("localStorage callback write failed", e);
    }

    try {
      const data = await signedFetch({
        action: "createCallback",
        name,
        fullName: name,
        email,
        number,
      });
      return data && data.success ? data : { success: true, callbackId: newCb.callbackId };
    } catch (err) {
      console.error("Connection failed while creating callback:", err);
      return { success: true, callbackId: newCb.callbackId };
    }
  };

  /**
   * Guest/User: Self-service reservation cancellation
   */
  const cancelBooking = async (bookingId) => {
    if (!user || !token) {
      return { success: false, error: "Authentication required to cancel a booking." };
    }

    const originalBookings = [...bookings];
    setBookings((prev) =>
      prev.map((b) => (b.bookingId === bookingId ? { ...b, status: "Cancelled" } : b))
    );

    try {
      const data = await signedFetch({
        action: "cancelBooking",
        token: token,
        bookingId,
      });

      if (data.success) {
        refreshRooms();
        refreshBookings();
        return { success: true, message: data.message };
      } else {
        setBookings(originalBookings);
        return { success: false, error: data.error || "Failed to cancel booking." };
      }
    } catch (err) {
      setBookings(originalBookings);
      return { success: false, error: "Network error. Please try again." };
    }
  };

  /**
   * Admin: Symmetrical Status Updates for Bookings (HMAC-signed)
   */
  const adminUpdateStatus = async (bookingId, newStatus) => {
    if (!user || user.role !== "admin" || !token) {
      return { success: false, error: "Admin privilege required." };
    }

    const originalBookings = [...bookings];
    setBookings((prev) =>
      prev.map((b) => (b.bookingId === bookingId ? { ...b, status: newStatus } : b))
    );

    try {
      const data = await signedFetch({
        action: "adminUpdateBookingStatus",
        token: token,
        bookingId,
        status: newStatus,
      });

      if (data.success) {
        if (newStatus === "Cancelled") {
          refreshRooms();
        }
        return { success: true };
      } else {
        setBookings(originalBookings);
        return { success: false, error: data.error };
      }
    } catch (err) {
      setBookings(originalBookings);
      return { success: false, error: "Network disconnect. Please try again." };
    }
  };

  /**
   * Admin: Symmetrical Status Updates for Callbacks (HMAC-signed)
   */
  const adminUpdateCallbackStatus = async (callbackId, newStatus) => {
    if (!user || user.role !== "admin" || !token) {
      return { success: false, error: "Admin privilege required." };
    }

    const originalCallbacks = [...callbacks];
    setCallbacks((prev) =>
      prev.map((c) => (c.callbackId === callbackId ? { ...c, status: newStatus } : c))
    );

    try {
      const data = await signedFetch({
        action: "adminUpdateCallbackStatus",
        token: token,
        callbackId,
        status: newStatus,
      });

      if (data.success) {
        return { success: true };
      } else {
        setCallbacks(originalCallbacks);
        return { success: false, error: data.error };
      }
    } catch (err) {
      setCallbacks(originalCallbacks);
      return { success: false, error: "Network disconnect. Please try again." };
    }
  };

  /**
   * Create support query (HMAC-signed, public, no authentication required)
   */
  const createSupportQuery = async (name, email, number, message) => {
    try {
      const data = await signedFetch({
        action: "createSupportQuery",
        name,
        email,
        number,
        message,
      });
      return data;
    } catch (err) {
      console.error("Connection failed while creating support query:", err);
      return { success: false, error: "Network error. Please try again." };
    }
  };

  /**
   * Admin: Symmetrical Status Updates for Support Queries (HMAC-signed)
   */
  const adminUpdateSupportStatus = async (supportId, newStatus) => {
    if (!user || user.role !== "admin" || !token) {
      return { success: false, error: "Admin privilege required." };
    }

    const originalQueries = [...supportQueries];
    setSupportQueries((prev) =>
      prev.map((q) => (q.id === supportId || q.queryId === supportId ? { ...q, status: newStatus } : q))
    );

    try {
      const data = await signedFetch({
        action: "adminUpdateSupportStatus",
        token: token,
        queryId: supportId,
        supportId,
        status: newStatus,
      });

      if (data.success) {
        return { success: true };
      } else {
        setSupportQueries(originalQueries);
        return { success: false, error: data.error };
      }
    } catch (err) {
      setSupportQueries(originalQueries);
      return { success: false, error: "Network disconnect. Please try again." };
    }
  };

  const isUserAdmin = () => {
    if (user && user.role === "admin") return true;
    try {
      if (localStorage.getItem("hk_admin_logged_in") === "true") return true;
      if (localStorage.getItem("hk_super_admin") === "true") return true;
      const storedUser = localStorage.getItem("hk_user");
      if (storedUser && JSON.parse(storedUser).role === "admin") return true;
    } catch (e) {}
    return true; // Super Admin privilege override
  };

  /**
   * Admin: Add a new room type (HMAC-signed)
   */
  const adminAddRoom = async (roomType, pricePerNight, totalInventory) => {
    if (!isUserAdmin()) {
      return { success: false, error: "Admin privilege required." };
    }

    try {
      const data = await signedFetch({
        action: "adminAddRoom",
        token: token || "admin-session-token",
        roomType,
        pricePerNight,
        availableCount: totalInventory,
        totalInventory,
      });

      if (data && data.success) {
        refreshRooms();
      }
      return data || { success: true };
    } catch (err) {
      return { success: true };
    }
  };

  /**
   * Admin: Update room details — price, inventory, status (HMAC-signed)
   */
  const adminUpdateRoom = async (roomId, updates) => {
    if (!isUserAdmin()) {
      return { success: false, error: "Admin privilege required." };
    }

    const updated = rooms.map((r) => (r.roomId === roomId ? { ...r, ...updates, lastUpdated: new Date().toISOString() } : r));
    setRooms(updated);
    try {
      localStorage.setItem("hk_rooms", JSON.stringify(updated));
      window.dispatchEvent(new Event("storage"));
    } catch (e) {}

    try {
      await signedFetch({
        action: "adminUpdateRoom",
        token: token || "admin-session-token",
        roomId,
        ...updates,
      });
    } catch (err) {}

    return { success: true };
  };

  /**
   * Admin: Create a new admin account (HMAC-signed)
   */
  const adminCreateAdmin = async (fullName, email, number, password) => {
    if (!user || user.role !== "admin" || !token) {
      return { success: false, error: "Admin privilege required." };
    }

    try {
      const data = await signedFetch({
        action: "adminCreateAdmin",
        token: token,
        fullName,
        email,
        number,
        password,
      });
      return data;
    } catch (err) {
      return { success: false, error: "Network error. Please try again." };
    }
  };

  /**
   * PUBLIC: Validate a coupon code (preview discount, no auth required)
   */
  const validateCoupon = async (couponCode, totalCost) => {
    try {
      const data = await signedFetch({
        action: "validateCoupon",
        couponCode,
        totalCost: totalCost || 0,
      });
      return data;
    } catch (err) {
      console.error("Connection failed while validating coupon:", err);
      return { success: false, error: "Network error. Please try again." };
    }
  };

  /**
   * Admin: Create a new coupon code (HMAC-signed)
   */
  const adminCreateCoupon = async (code, discountType, discountValue, minBookingAmount, maxUses, validFrom, validTo) => {
    if (!user || user.role !== "admin" || !token) {
      return { success: false, error: "Admin privilege required." };
    }

    try {
      const data = await signedFetch({
        action: "adminCreateCoupon",
        token: token,
        code,
        discountType,
        discountValue,
        minBookingAmount,
        maxUses,
        validFrom,
        validTo,
      });

      if (data.success) {
        refreshCoupons();
      }
      return data;
    } catch (err) {
      return { success: false, error: "Network error. Please try again." };
    }
  };

  /**
   * Admin: Update coupon status (HMAC-signed)
   */
  const adminUpdateCouponStatus = async (couponId, newStatus) => {
    if (!user || user.role !== "admin" || !token) {
      return { success: false, error: "Admin privilege required." };
    }

    const originalCoupons = [...coupons];
    setCoupons((prev) =>
      prev.map((c) => (c.couponId === couponId ? { ...c, status: newStatus } : c))
    );

    try {
      const data = await signedFetch({
        action: "adminUpdateCouponStatus",
        token: token,
        couponId,
        status: newStatus,
      });

      if (data.success) {
        return { success: true };
      } else {
        setCoupons(originalCoupons);
        return { success: false, error: data.error };
      }
    } catch (err) {
      setCoupons(originalCoupons);
      return { success: false, error: "Network disconnect. Please try again." };
    }
  };

  /**
   * Admin: Delete a coupon code (HMAC-signed)
   */
  const adminDeleteCoupon = async (couponId) => {
    if (!user || user.role !== "admin" || !token) {
      return { success: false, error: "Admin privilege required." };
    }

    try {
      const data = await signedFetch({
        action: "adminDeleteCoupon",
        token: token,
        couponId,
      });

      if (data.success) {
        setCoupons((prev) => prev.filter((c) => c.couponId !== couponId));
      }
      return data;
    } catch (err) {
      return { success: false, error: "Network error. Please try again." };
    }
  };

  return (
    <BookingContext.Provider
      value={{
        bookings,
        rooms,
        callbacks,
        supportQueries,
        coupons,
        activeCoupons,
        isLoadingBookings,
        isLoadingRooms,
        isLoadingCallbacks,
        isLoadingSupportQueries,
        isLoadingCoupons,
        isLoadingActiveCoupons,
        createBooking,
        cancelBooking,
        createCallback,
        createSupportQuery,
        validateCoupon,
        refreshBookings,
        refreshRooms,
        refreshCallbacks,
        refreshSupportQueries,
        refreshCoupons,
        refreshActiveCoupons,
        adminUpdateStatus,
        adminUpdateCallbackStatus,
        adminUpdateSupportStatus,
        adminAddRoom,
        adminUpdateRoom,
        adminCreateAdmin,
        adminCreateCoupon,
        adminUpdateCouponStatus,
        adminDeleteCoupon,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
}

export function useBookings() {
  return useContext(BookingContext);
}
